import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@repo/design-system/utils";
import { SlidersHorizontal } from "lucide-react";
import { memo, type ReactNode } from "react";

export interface CartToolbarProps {
  /**
   * Whether to offer the Filter popover. The toolbar can still be shown for the
   * view toggle alone (e.g. a single-supplier cart deep-linked to Compare needs
   * the toggle to get back), so the Filter control is gated separately from the
   * toolbar's own visibility.
   */
  showFilter: boolean;
  /**
   * Whether any filter is currently narrowing the cart. Badges the Filter
   * button so an applied filter is visible without opening the popover.
   */
  hasActiveFilter?: boolean;
  /**
   * The filter surface rendered inside the Filter popover - the reused
   * `CartFilterSidebar`, its predicates unchanged (KAN-71). The rail is gone;
   * its content lives here now.
   */
  filterContent: ReactNode;
  /** Slot for the Cart / Compare view toggle (KAN-72). */
  viewToggle?: ReactNode;
  /**
   * Optional context between the Filter button and the view toggle (the Compare
   * view's legend and hint). From `lg` it shares the row, in the space the two
   * controls leave empty; below `lg` it drops to its own full-width row so the
   * controls keep their room.
   */
  children?: ReactNode;
  className?: string;
}

/**
 * The cart's slim toolbar (KAN-71). It replaces the permanent 256px filter
 * rail: a Filter popover on the left and (KAN-72) the Cart / Compare toggle on
 * the right - so the cart itself gets the full width and the filters are there
 * when wanted, gone when not. The row fills the card, so the Filter button
 * lines up with the page content. CartPage only mounts it when there is
 * actually something to filter or compare (the cart spans multiple suppliers or
 * categories).
 */
export const CartToolbar = memo(function CartToolbar({
  showFilter,
  hasActiveFilter = false,
  filterContent,
  viewToggle,
  children,
  className
}: CartToolbarProps) {
  return (
    <div
      className={cn(
        "mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 rounded-xl border border-gray-200 bg-white px-4 py-2",
        className
      )}
    >
      {showFilter && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
            >
              <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
              Filter
              {hasActiveFilter && (
                <span
                  className="h-1.5 w-1.5 rounded-full bg-purple-600"
                  aria-hidden="true"
                />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            collisionPadding={16}
            // Cap the height to the space actually below the trigger - Radix
            // measures it to the viewport edge and exposes it as this var -
            // not a fixed 70vh. A cart low on a short window otherwise pushes
            // the list past the bottom edge, where the part you need to scroll
            // to sits off-screen and can't be reached. Scrolls within whatever
            // fits; still capped at 32rem on a tall screen.
            className="max-h-[min(32rem,var(--radix-popover-content-available-height))] w-80 overflow-y-auto"
          >
            {filterContent}
          </PopoverContent>
        </Popover>
      )}
      {children && (
        <div className="order-last flex w-full flex-wrap items-center gap-x-4 gap-y-1 lg:order-none lg:w-auto lg:flex-1">
          {children}
        </div>
      )}
      {viewToggle}
    </div>
  );
});
